import { PureComponent } from 'react'
import PropTypes from 'prop-types'
import _ from 'lodash'
import classNames from 'classnames'
import { ResponsiveContainer, Surface, Symbols, PieChart, Pie, Tooltip, Cell, Legend } from 'recharts'
import PieTable from '../../../Common/NewDashboard/Table/PieTable/PieTable'
import PieTableStyles from '../../../Common/NewDashboard/Table/PieTable/PieTable.css'
import PieLabel from '../../../Common/NewDashboard/Chart/PieLabel/PieLabel'
import styles from './SaleAnalysisChart.css'
import { CustomTooltip } from '../Shared'
import { SALE_FILTER_TYPE_MAP } from '../../../../constants/globalTypes'
import { numeralFormat } from '../../../../../lib/renderUtil'
import Popover from '../../../Common/Popover'
import { pullLeft, pullRight } from '../../../../styles/shared.css'

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', 'maroon']
const SIZE = 32

class CustomizedLegendContent extends PureComponent {
  static displayName = 'CustomizedLegend'

  static propTypes = {
    content: PropTypes.element,
    iconSize: PropTypes.number,
    iconType: PropTypes.string,
    layout: PropTypes.oneOf(['horizontal', 'vertical']),
    align: PropTypes.oneOf(['center', 'left', 'right']),
    verticalAlign: PropTypes.oneOf(['top', 'bottom', 'middle']),
    payload: PropTypes.arrayOf(PropTypes.shape({
      value: PropTypes.any,
      id: PropTypes.any,
      type: PropTypes.any
    })),
    inactiveColor: PropTypes.string,
    formatter: PropTypes.func,
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    onClick: PropTypes.func
  }

  static defaultProps = {
    iconSize: 14,
    layout: 'horizontal',
    align: 'center',
    verticalAlign: 'middle',
    inactiveColor: '#ccc'
  }

  /**
   * Render the path of icon
   * @param {Object} data Data of each legend item
   * @return {String} Path element
   */
  renderIcon (data) {
    const { inactiveColor } = this.props
    const halfSize = SIZE / 2
    const sixthSize = SIZE / 6
    const thirdSize = SIZE / 3
    const color = data.inactive ? inactiveColor : data.color

    if (data.type === 'line') {
      return (
        <path
          strokeWidth={4}
          fill='none'
          stroke={color}
          d={`M0,${halfSize}h${thirdSize}
            A${sixthSize},${sixthSize},0,1,1,${2 * thirdSize},${halfSize}
            H${SIZE}M${2 * thirdSize},${halfSize}
            A${sixthSize},${sixthSize},0,1,1,${thirdSize},${halfSize}`}
          className='recharts-legend-icon'
        />
      )
    } else if (data.type === 'rect') {
      return (
        <path
          stroke='none'
          fill={color}
          d={`M0,${SIZE / 8}h${SIZE}v${SIZE * 3 / 4}h${-SIZE}z`}
          className='recharts-legend-icon'
        />
      )
    }

    return (
      <Symbols
        fill={color}
        cx={halfSize}
        cy={halfSize}
        size={SIZE}
        sizeType='diameter'
        type={data.type}
      />
    )
  }

  /**
   * Generate column of table
   */
  generateTableColumns = () => {
    const { valueType, iconSize, layout } = this.props
    const viewBox = { x: 0, y: 0, width: SIZE, height: SIZE }
    const itemStyle = {
      display: layout === 'horizontal' ? 'inline-block' : 'block'
    }
    const svgStyle = { display: 'inline-block', verticalAlign: 'middle', marginRight: 12 }

    const categoryCell = (row) => {
      const entry = row.original
      const className = classNames(
        styles.categoryLegendItem,
        {
          inactive: entry.inactive
        })

      if (entry.type === 'none') {
        return null
      }
      // generate svg icon before the text
      return (
        <span
          className={className}
          style={itemStyle}
        >
          <Surface
            width={iconSize}
            height={iconSize}
            viewBox={viewBox}
            style={svgStyle}
          >
            {this.renderIcon(entry)}
          </Surface>
          <Popover content={entry.name}>
            <span
              className='recharts-legend-item-text'
            >
              {entry.name}
            </span>
          </Popover>
        </span>
      )
    }
    const valueTypeHeader = SALE_FILTER_TYPE_MAP[valueType].text

    return [{
      Header: '分類',
      headerClassName: classNames(pullLeft, PieTableStyles.col, PieTableStyles.start),
      className: classNames(pullLeft, PieTableStyles.col, PieTableStyles.start),
      width: 185,
      maxWidth: 185,
      Cell: categoryCell
    }, {
      id: 'value',
      Header: valueTypeHeader,
      headerClassName: classNames(pullRight, PieTableStyles.col),
      className: classNames(pullRight, PieTableStyles.col),
      width: 110,
      maxWidth: 110,
      accessor: d => {
        const formatedVal = numeralFormat(d.value)
        return (
          <span>{formatedVal}</span>
        )
      }
    }, {
      id: 'percent',
      Header: '佔比',
      headerClassName: classNames(pullRight, PieTableStyles.col),
      className: classNames(pullRight, PieTableStyles.col),
      accessor: d => `${formatPercent(d.percent)}%`,
      width: 70
    }]
  }

  render () {
    const { payload, layout, align } = this.props

    if (!payload || !payload.length) { return null }

    const finalStyle = {
      padding: 0,
      margin: 0,
      textAlign: layout === 'horizontal' ? align : 'left'
    }
    const data = _.map(payload, (entry, idx) => {
      return {
        ..._.pick(entry, ['color', 'type']),
        ..._.pick(entry.payload, ['percent', 'name', 'value'])
      }
    })

    return (
      <ul
        className={classNames('recharts-customized-legend', styles.customizedLegend)}
        style={finalStyle}
      >
        <PieTable
          data={data}
          defaultPageSize={_.size(data)}
          columns={this.generateTableColumns()}
        />
      </ul>
    )
  }
}

const formatPercent = (percent) => (percent * 100).toFixed(2)

class SaleAnalysisChart extends PureComponent {
  constructor (props) {
    super(props)
    this.state = {
      width: 945,
      height: 300,
      data: []
    }
  }

  checkIsLabelVisible = chartProps => {
    const { percent } = chartProps
    // 百分比低於五個數據則不秀出
    const isLabelVisible = (+percent >= 5)
    return isLabelVisible
  }

  getTopPies = () => {
    const { data } = this.props
    const topPies = _.flow(
      data => _.orderBy(data, ['value'], ['desc']),
      sortedData => _.slice(sortedData, 0, 3)
    )(data)
    return topPies
  }

  render () {
    const { data, ...props } = this.props
    const topPies = this.getTopPies()

    return (
      <ResponsiveContainer
        width='100%'
        height='100%'
        minHeight='350px'
      >
        <PieChart>
          <Pie
            dataKey='value'
            isAnimationActive={false}
            data={data}
            cx='50%'
            cy='50%'
            outerRadius={100}
            labelLine={false}
            label={(
              <PieLabel
                topPies={topPies}
                lineSize={8}
                formatPercent={formatPercent}
                checkIsVisible={this.checkIsLabelVisible}
              />
            )}
            fill='#8884d8'
            startAngle={90}
            endAngle={-270}
          >
            {
              data.map((entry, index) => (
                <Cell
                  key={`pie-piece-${index}`}
                  stroke=''
                  fill={COLORS[index % COLORS.length]}
                />
              ))
            }
          </Pie>
          <Legend
            layout='vertical'
            align='right'
            verticalAlign='top'
            wrapperStyle={{ top: 32, right: 0 }}
            iconSize={16}
            iconType='square'
            content={<CustomizedLegendContent {...props} />}
          />
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
    )
  }
}

export default SaleAnalysisChart
